// TaskInputRenderer - Coordinator component for controlled task inputs in modals
// Uses ui_component property to render the appropriate input component

import { Box, Text } from '@chakra-ui/react';
import type { WebTaskItem, MediaPayload } from '@/lib/sdk/workflows/types';
import { getChoiceOptionsWithIds, ChoiceOption } from '@/lib/sdk/chatbot/types';
import { TextInputTask } from './TextInputTask';
import { NumberInputTask } from './NumberInputTask';
import { ChoiceTask } from './ChoiceTask';
import { DateInputTask } from './DateInputTask';
import { WhatsAppUploadTask } from './WhatsAppUploadTask';

interface TaskInputRendererProps {
  task: WebTaskItem;
  onTextValue: (value: string) => void;
  onMediaValue: (media: MediaPayload | null, isUploading: boolean) => void;
  onChoiceValue: (choice: ChoiceOption | null) => void;
  initialTextValue?: string;
  initialChoice?: ChoiceOption;
  initialMedia?: MediaPayload;
  sopAddMoreMode?: boolean; // When true, show combined text+media input for SOP completed tasks
}

/**
 * Renders the appropriate input component based on task.ui_component
 * All components are rendered in controlled mode (no submit buttons)
 */
export function TaskInputRenderer({
  task,
  onTextValue,
  onMediaValue,
  onChoiceValue,
  initialTextValue = '',
  initialChoice,
  initialMedia,
  sopAddMoreMode = false,
}: TaskInputRendererProps) {
  const { ui_component, validation, task: taskData } = task;

  // For SOP "Add More" mode, show combined text+media input regardless of ui_component
  // This allows users to add text notes, media, or both
  if (sopAddMoreMode) {
    return (
      <Box>
        <TextInputTask
          description="Add a note"
          validation={{ ...validation, required: false }}
          onSubmit={() => {}}
          controlled
          onValueChange={onTextValue}
          initialValue={initialTextValue}
        />
        <Box mt={4}>
          <WhatsAppUploadTask
            taskName={taskData.task_name}
            botCode={taskData.chatbot_code}
            taskId={taskData.task_id}
            repId={Number(taskData.id)}
            mediaType="media_upload"
          />
        </Box>
      </Box>
    );
  }

  // Get choices from task notes where is_custom_data is true
  const getChoices = (): ChoiceOption[] => {
    if (taskData.notes && Array.isArray(taskData.notes)) {
      return getChoiceOptionsWithIds(taskData.notes);
    }
    // Fallback to validation.accept if notes don't have choices
    if (validation?.accept) {
      return validation.accept.map((text, idx) => ({
        id: `fallback_${idx}`,
        text,
      }));
    }
    return [];
  };

  // Render appropriate component based on ui_component type
  switch (ui_component) {
    case 'text_input':
      return (
        <TextInputTask
          description={taskData.task_name}
          validation={validation}
          onSubmit={() => {}} // Not used in controlled mode
          controlled
          onValueChange={onTextValue}
          initialValue={initialTextValue}
        />
      );

    case 'number_input':
      return (
        <NumberInputTask
          description={taskData.task_name}
          validation={validation}
          onSubmit={() => {}} // Not used in controlled mode
          controlled
          onValueChange={onTextValue}
          initialValue={initialTextValue}
        />
      );

    case 'date_picker':
      return (
        <DateInputTask
          description={taskData.task_name}
          validation={validation}
          onSubmit={() => {}} // Not used in controlled mode
          controlled
          onValueChange={onTextValue}
          initialValue={initialTextValue}
        />
      );

    case 'choice_selector':
      return (
        <ChoiceTask
          description={taskData.task_name}
          choices={getChoices()}
          validation={validation}
          onSubmit={() => {}} // Not used in controlled mode
          controlled
          onChoiceChange={onChoiceValue}
          initialChoice={initialChoice}
        />
      );

    case 'photo_upload':
    case 'video_upload':
    case 'audio_upload':
    case 'document_upload':
    case 'media_upload':
      return (
        <WhatsAppUploadTask
          taskName={taskData.task_name}
          botCode={taskData.chatbot_code}
          taskId={taskData.task_id}
          repId={Number(taskData.id)}
          mediaType={ui_component}
        />
      );

    case 'optional_input':
      return (
        <Box>
          <TextInputTask
            description={taskData.task_name}
            validation={{ ...validation, required: false }}
            onSubmit={() => {}} // Not used in controlled mode
            controlled
            onValueChange={onTextValue}
            initialValue={initialTextValue}
          />
          <Box mt={4}>
            <WhatsAppUploadTask
              taskName={taskData.task_name}
              botCode={taskData.chatbot_code}
              taskId={taskData.task_id}
              repId={Number(taskData.id)}
              mediaType="media_upload"
            />
          </Box>
        </Box>
      );

    case 'location_picker':
    case 'address_input':
    case 'location_check':
      return (
        <Box>
          <Text fontSize="sm" color="text-secondary" mb={2}>
            Location input not yet implemented in web interface.
            Please use WhatsApp for location-based tasks.
          </Text>
          <TextInputTask
            description=""
            validation={{ ...validation, required: false }}
            onSubmit={() => {}}
            controlled
            onValueChange={onTextValue}
            initialValue={initialTextValue}
          />
        </Box>
      );

    default:
      // Fallback to text input for unknown types
      return (
        <Box>
          <Text fontSize="xs" color="text-muted" mb={2}>
            Task type: {ui_component}
          </Text>
          <TextInputTask
            description={taskData.task_name}
            validation={validation}
            onSubmit={() => {}}
            controlled
            onValueChange={onTextValue}
            initialValue={initialTextValue}
          />
        </Box>
      );
  }
}